/**
 * All rights reserved exactmatch marketing
 * Lightweight Markdown -> HTML renderer with built-in sanitization.
 *
 * Supports:
 * - Headings (# .. ######)
 * - Horizontal rules (---, ***, ___)
 * - Blockquotes (> nested)
 * - Fenced code blocks (```lang)
 * - Inline code (`code`)
 * - Bold/Italic/Strikethrough (**, *, __, _, ~~)
 * - Ordered/Unordered/Task lists (supports nesting via indentation)
 * - Links and Images ([text](url), ![alt](src))
 * - Autolinks for bare URLs and <http://...>
 * - Tables (GitHub-style alignment)
 * - Paragraphs and line breaks
 * - XSS sanitization for produced HTML (no external deps)
 *
 * Notes:
 * - This is not a full CommonMark implementation but aims to cover
 *   the majority of practical cases without external libraries.
 */

/* eslint-disable no-useless-escape */

const DEFAULT_OPTS = {
  allowHtml: false, // if true, raw HTML blocks are allowed (still sanitized)
  breaks: true, // convert single newlines to <br> inside paragraphs
  linkify: true, // auto-link bare URLs
  sanitize: true, // sanitize the final HTML
  openLinksInNewTab: true,
  addNoFollow: true,
};

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function normalizeUrl(url) {
  if (!url) return '';
  const trimmed = url.trim();
  // Block dangerous protocols
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:')
  ) {
    return '#';
  }
  return trimmed;
}

function sanitizeHtml(html, { openLinksInNewTab = true, addNoFollow = true } = {}) {
  if (!html) return '';

  if (!isBrowser()) {
    // Basic fallback when DOM not available: drop script/style tags explicitly
    return html
      .replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
      .replace(/<\s*style[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, '')
      .replace(/on[a-z]+\s*=\s*"[^"]*"/gi, '')
      .replace(/on[a-z]+\s*=\s*'[^']*'/gi, '')
      .replace(/on[a-z]+\s*=\s*[^\s>]+/gi, '');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild; // wrapping div

  const BLOCKED_TAGS = new Set([
    'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM'
  ]);

  const ALLOWED_ATTR = new Set([
    'href', 'src', 'alt', 'title', 'class', 'id', 'width', 'height', 'align', 'lang', 'dir'
  ]);

  const traverse = (node) => {
    if (node.nodeType === 1) { // Element
      const el = node;
      if (BLOCKED_TAGS.has(el.tagName)) {
        el.remove();
        return;
      }

      // Remove event handlers and unknown attrs
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || name === 'style' || name === 'srcdoc') {
          el.removeAttribute(attr.name);
          continue;
        }
        if (!ALLOWED_ATTR.has(attr.name)) {
          // Keep data-* attributes
          if (!name.startsWith('data-')) {
            el.removeAttribute(attr.name);
          }
        }
      }

      // Fix URLs and link rel/target
      if (el.tagName === 'A' && el.hasAttribute('href')) {
        const href = normalizeUrl(el.getAttribute('href'));
        el.setAttribute('href', href);
        if (openLinksInNewTab) el.setAttribute('target', '_blank');
        if (addNoFollow) {
          const existing = (el.getAttribute('rel') || '').split(/\s+/);
          const rel = new Set(existing.filter(Boolean));
          rel.add('noopener');
          rel.add('noreferrer');
          rel.add('nofollow');
          el.setAttribute('rel', Array.from(rel).join(' '));
        }
      }

      if (el.tagName === 'IMG' && el.hasAttribute('src')) {
        const src = normalizeUrl(el.getAttribute('src'));
        el.setAttribute('src', src);
      }
    }

    // Traverse children (convert to array as live list changes when removing)
    for (const child of Array.from(node.childNodes)) traverse(child);
  };

  traverse(root);
  return root.innerHTML;
}

function inlineMarkdown(text, opts) {
  const { linkify } = opts;
  let out = text || '';

  // Escape HTML first, unless allowHtml explicitly true
  out = escapeHtml(out);

  // Images ![alt](url "title")
  out = out.replace(/!\[([^\]]*)\]\((\S+?)(?:\s+"([^"]+)")?\)/g, (m, alt, src, title) => {
    const s = normalizeUrl(src);
    const t = title ? ` title="${escapeHtml(title)}"` : '';
    return `<img src="${escapeHtml(s)}" alt="${escapeHtml(alt)}"${t}>`;
  });

  // Links [text](url "title")
  out = out.replace(/\[([^\]]+)\]\((\S+?)(?:\s+"([^"]+)")?\)/g, (m, text1, href, title) => {
    const h = normalizeUrl(href);
    const t = title ? ` title="${escapeHtml(title)}"` : '';
    return `<a href="${escapeHtml(h)}"${t}>${escapeHtml(text1)}</a>`;
  });

  // Autolink <http://...>
  out = out.replace(/<((https?:)\/\/[^\s>]+)>/g, (m, url) => `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`);

  // Strikethrough ~~text~~
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  // Bold **text** or __text__
  out = out.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '<strong>$2</strong>');

  // Italic *text* or _text_
  out = out.replace(/(\*|_)(?=\S)([\s\S]*?\S)\1/g, '<em>$2</em>');

  // Inline code `code`
  out = out.replace(/`([^`]+?)`/g, (m, code) => `<code>${escapeHtml(code)}</code>`);

  if (linkify) {
    // Bare URL linkify (avoid already linked ones)
    out = out.replace(/(^|[^\">])(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g, (m, lead, url) => {
      return `${lead}<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`;
    });
  }

  return out;
}

function parseTableRow(line) {
  // Split respecting pipes, allow empty cells
  const trimmed = line.trim();
  const noBoundaries = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return noBoundaries.split(/\s*\|\s*/).map((s) => s.trim());
}

function isTableSeparator(line) {
  const trimmed = line.trim();
  if (!/^\|?\s*[:\-\| ]+\s*\|?$/.test(trimmed)) return false;
  // Ensure every segment has --- optionally with : at either end
  const cells = parseTableRow(trimmed);
  return cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c));
}

function renderMarkdown(input, userOptions = {}) {
  const opts = { ...DEFAULT_OPTS, ...userOptions };
  const lines = (input || '').replace(/\r\n?/g, '\n').split('\n');
  const html = [];

  let i = 0;
  let inCode = false;
  let codeLang = '';
  let codeBuffer = [];
  const listStack = []; // { type: 'ul'|'ol', indent: number }
  let pendingParagraph = [];
  let inTable = false;
  let table = null; // { headers: [], aligns: [], rows: [] }

  const closeParagraph = () => {
    if (pendingParagraph.length) {
      const text = pendingParagraph.join(opts.breaks ? '<br>\n' : '\n');
      
      // Check if the paragraph contains HTML tags (br or img)
      const hasHtmlTags = /<(br|img)[^>]*>/i.test(text);
      
      if (hasHtmlTags) {
        // For paragraphs with HTML content, we need to process it differently
        // First, escape any non-HTML content, then allow the HTML tags to pass through
        let processedText = text;
        
        // Split the text by HTML tags to process markdown in between
        const parts = processedText.split(/(<(?:br|img)[^>]*>)/gi);
        const processedParts = parts.map((part, index) => {
          // If this part is an HTML tag, keep it as is
          if (/<(?:br|img)[^>]*>/i.test(part)) {
            return part;
          }
          // Otherwise, process it as markdown
          return inlineMarkdown(part, opts);
        });
        
        html.push(`<p>${processedParts.join('')}</p>`);
      } else {
        // Normal markdown processing for paragraphs without HTML
        html.push(`<p>${inlineMarkdown(text, opts)}</p>`);
      }
      
      pendingParagraph = [];
    }
  };

  const closeListsToIndent = (indent) => {
    while (listStack.length && listStack[listStack.length - 1].indent >= indent) {
      const last = listStack.pop();
      html.push(`</${last.type}>`);
    }
  };

  const openList = (type, indent) => {
    listStack.push({ type, indent });
    html.push(`<${type}>`);
  };

  const pushListItem = (content) => {
    html.push(`<li>${inlineMarkdown(content, opts)}</li>`);
  };

  const closeAllLists = () => closeListsToIndent(-1);

  const closeTable = () => {
    if (!inTable || !table) return;
    const aligns = table.aligns.map((a) => (a === 'left' || a === 'center' || a === 'right' ? a : null));
    const thead = `<thead><tr>${table.headers
      .map((h, idx) => `<th${aligns[idx] ? ` style=\"text-align:${aligns[idx]}\"` : ''}>${inlineMarkdown(h, opts)}</th>`)
      .join('')}</tr></thead>`;
    const tbody = `<tbody>${table.rows
      .map((row) => `<tr>${row
        .map((c, idx) => `<td${aligns[idx] ? ` style=\"text-align:${aligns[idx]}\"` : ''}>${inlineMarkdown(c, opts)}</td>`)
        .join('')}</tr>`)
      .join('')}</tbody>`;
    html.push(`<table>${thead}${tbody}</table>`);
    inTable = false;
    table = null;
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw; // keep raw for code blocks

    // Handle fenced code blocks
    const fenceMatch = line.match(/^\s*```(.*)$/);
    if (fenceMatch) {
      if (!inCode) {
        closeParagraph();
        closeAllLists();
        closeTable();
        inCode = true;
        codeLang = (fenceMatch[1] || '').trim();
        codeBuffer = [];
        i += 1;
        continue;
      } else {
        // Close code block
        const codeHtml = escapeHtml(codeBuffer.join('\n'));
        const cls = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : '';
        html.push(`<pre><code${cls}>${codeHtml}\n</code></pre>`);
        inCode = false;
        codeLang = '';
        codeBuffer = [];
        i += 1;
        continue;
      }
    }

    if (inCode) {
      codeBuffer.push(line);
      i += 1;
      continue;
    }

    // Blank line closes paragraphs/lists/tables
    if (/^\s*$/.test(line)) {
      closeParagraph();
      closeAllLists();
      closeTable();
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^\s*(\*\s*\*\s*\*|-\s*-\s*-|_\s*_\s*_)\s*$/.test(line)) {
      closeParagraph();
      closeAllLists();
      closeTable();
      html.push('<hr>');
      i += 1;
      continue;
    }

    // Headings
    const h = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
    if (h) {
      closeParagraph();
      closeAllLists();
      closeTable();
      const level = h[1].length;
      const content = h[2].trim().replace(/\s+#\s*$/, '');
      html.push(`<h${level}>${inlineMarkdown(content, opts)}</h${level}>`);
      i += 1;
      continue;
    }

    // Blockquote (support basic nesting)
    const bq = line.match(/^\s{0,3}>(.*)$/);
    if (bq) {
      closeParagraph();
      closeAllLists();
      closeTable();
      // Collect contiguous blockquote lines
      const quoteLines = [];
      while (i < lines.length) {
        const l = lines[i];
        const m = l.match(/^\s{0,3}>(.*)$/);
        if (!m) break;
        quoteLines.push(m[1].replace(/^\s/, ''));
        i += 1;
      }
      const quoteHtml = renderMarkdown(quoteLines.join('\n'), opts);
      html.push(`<blockquote>${quoteHtml}</blockquote>`);
      continue;
    }

    // Tables (GFM)
    if (!inTable) {
      const next = lines[i + 1];
      if (next && isTableSeparator(next)) {
        closeParagraph();
        closeAllLists();
        inTable = true;
        const headers = parseTableRow(line);
        const aligns = parseTableRow(next).map((c) => (c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : c.startsWith(':') ? 'left' : null));
        table = { headers, aligns, rows: [] };
        i += 2;
        // consume rows
        while (i < lines.length && /\|/.test(lines[i]) && !/^\s*$/.test(lines[i])) {
          table.rows.push(parseTableRow(lines[i]));
          i += 1;
        }
        closeTable();
        continue;
      }
    }

    // Lists
    const listMatch = line.match(/^(\s*)([*+-]|\d+\.)\s+(.*)$/);
    if (listMatch) {
      closeParagraph();
      closeTable();
      const indentSpaces = listMatch[1].replace(/\t/g, '    ').length;
      const type = /\d+\./.test(listMatch[2]) ? 'ol' : 'ul';
      const content = listMatch[3];

      // manage nesting by indentation (2+ spaces)
      const currentIndent = Math.floor(indentSpaces / 2);
      if (!listStack.length || listStack[listStack.length - 1].indent < currentIndent || listStack[listStack.length - 1].type !== type) {
        openList(type, currentIndent);
      } else if (listStack[listStack.length - 1].indent > currentIndent) {
        closeListsToIndent(currentIndent);
        if (!listStack.length || listStack[listStack.length - 1].type !== type) openList(type, currentIndent);
      }

      // Task list checkbox
      const task = content.match(/^\[( |x|X)\]\s+(.*)$/);
      if (task) {
        const checked = task[1].toLowerCase() === 'x';
        const label = inlineMarkdown(task[2], opts);
        html.push(`<li><input type="checkbox" disabled ${checked ? 'checked' : ''}> ${label}</li>`);
      } else {
        pushListItem(content);
      }

      i += 1;
      // consume following indented lines as part of same list item? simplified: handled as separate items
      continue;
    }

    // Paragraph (accumulate)
    pendingParagraph.push(line);
    i += 1;
  }

  // Close open structures
  if (inCode) {
    const codeHtml = escapeHtml(codeBuffer.join('\n'));
    const cls = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : '';
    html.push(`<pre><code${cls}>${codeHtml}\n</code></pre>`);
  }
  closeParagraph();
  closeAllLists();
  closeTable();

  const joined = html.join('\n');
  return opts.sanitize ? sanitizeHtml(joined, opts) : joined;
}

export { renderMarkdown, sanitizeHtml };
export default renderMarkdown;

